<template>
  <v-card class="pa-6">
    <v-card-title class="text-h5 text-primary mb-4">
      Inscrição — Aluno Especial
    </v-card-title>

    <v-card-text>
      <v-form ref="form" v-model="valid" lazy-validation>

        <v-overlay :value="loading" absolute>
          <v-progress-circular indeterminate size="64" color="primary" />
          <span class="ml-4 text-h6">Carregando dados...</span>
        </v-overlay>

        <!-- DISCIPLINAS -->
        <div class="section-header mb-4">Disciplinas</div>

        <v-select v-model="form.requested_disciplines" :items="classes" item-title="label" item-value="id"
          label="Disciplinas *" multiple chips clearable :loading="loadingClasses" @update:modelValue="onRequestedDisciplinesChange"
          :rules="[
            v => Array.isArray(v) && v.length > 0 || 'Selecione ao menos uma disciplina',
            v => Array.isArray(v) && v.length <= 2 || 'Selecione no máximo 2 disciplinas'
          ]" />

        <v-textarea v-model="form.reason_to_take_discipline" label="Motivo para fazer a disciplina *"
          :rules="[v => !!v || 'Campo obrigatório']" rows="4" />

        <!-- GRADUAÇÃO -->
        <div class="section-header mb-4">Formação Acadêmica — Graduação</div>

        <v-text-field v-model="form.graduation_course" label="Curso *" :rules="[v => !!v || 'Campo obrigatório']" />

        <v-text-field v-model="form.graduation_institution" label="Instituição *"
          :rules="[v => !!v || 'Campo obrigatório']" />

        <v-text-field v-model="form.graduation_year" label="Ano de conclusão *" type="number" @input="form.graduation_year = String(form.graduation_year).slice(0, 4)" :rules="[
          v => !!v || 'Campo obrigatório',
          v => Number.isInteger(Number(v)) || 'Ano inválido',
          v => v >= 1950 || 'Ano mínimo: 1950',
          v => v <= new Date().getFullYear() || 'Ano não pode ser futuro'
        ]" />

        <!-- PÓS-GRADUAÇÃO -->
        <div class="section-header mb-4">Formação Acadêmica — Pós-Graduação</div>

        <v-radio-group v-model="form.is_postgraduate_student" row>
          <v-radio label="Não" :value="false" />
          <v-radio label="Sim" :value="true" />
        </v-radio-group>

        <v-row dense>
          <v-col cols="12" v-show="form.is_postgraduate_student">
            <v-text-field v-model="form.postgraduate_course" label="Curso *"
              :rules="[v => !form.is_postgraduate_student || !!v || 'Campo obrigatório']" />
          </v-col>

          <v-col cols="12" v-show="form.is_postgraduate_student">
            <v-text-field v-model="form.postgraduate_advisor" label="Orientador *"
              :rules="[v => !form.is_postgraduate_student || !!v || 'Campo obrigatório']" />
          </v-col>

          <v-col cols="12" v-show="form.is_postgraduate_student">
            <v-text-field v-model="form.postgraduate_project_title" label="Nome do projeto *"
              :rules="[v => !form.is_postgraduate_student || !!v || 'Campo obrigatório']" />
          </v-col>

          <v-col cols="12" v-show="form.is_postgraduate_student">
            <v-text-field v-model="form.postgraduate_semesters_completed" type="number" label="Semestres já cursados *" @input="form.postgraduate_semesters_completed = String(form.postgraduate_semesters_completed).slice(0, 2)"
              :rules="[
                v => !form.is_postgraduate_student || !!v || 'Campo obrigatório',
                v => !form.is_postgraduate_student || Number.isInteger(Number(v)) || 'Número inválido',
                v => !form.is_postgraduate_student || (v >= 0 && v <= 20) || 'Valor entre 0 e 20'
              ]" />
          </v-col>
        </v-row>

        <!-- EMPREGO -->
        <div class="section-header mb-4">Emprego</div>

        <v-radio-group v-model="form.is_employed" row>
          <v-radio label="Não" :value="false" />
          <v-radio label="Sim" :value="true" />
        </v-radio-group>

        <v-row dense>
          <v-col cols="12" v-show="form.is_employed">
            <v-select v-model="form.job_type" :items="['Público', 'Privado', 'Autônomo']" label="Tipo de emprego *"
              :rules="[v => !form.is_employed || !!v || 'Campo obrigatório']" />
          </v-col>

          <v-col cols="12" v-show="form.is_employed">
            <v-text-field v-model="form.workplace" label="Instituição de trabalho *"
              :rules="[v => !form.is_employed || !!v || 'Campo obrigatório']" />
          </v-col>

          <v-col cols="12" v-show="form.is_employed">
            <v-text-field v-model="form.job_position" label="Cargo/Função *"
              :rules="[v => !form.is_employed || !!v || 'Campo obrigatório']" />
          </v-col>

          <v-col cols="12" v-show="form.is_employed">
            <v-text-field v-model="form.working_since" type="date" label="Neste trabalho desde *"
              :rules="[v => !form.is_employed || !!v || 'Campo obrigatório']" />
          </v-col>
        </v-row>

        <!-- NECESSIDADES ESPECIAIS
        <div class="section-header mb-4">Você possui alguma necessidade especial para a realização de alguma das fases do processo seletivo?</div>

        <v-radio-group v-model="form.has_special_needs" row>
          <v-radio label="Não" :value="false" />
          <v-radio label="Sim" :value="true" />
        </v-radio-group>

        <v-textarea v-show="form.has_special_needs" v-model="form.special_needs_description"
          label="Por favor, especifique:"
          :rules="[v => !form.has_special_needs || !!v || 'Campo obrigatório']" rows="4" />
        -->

        <!-- TERMO DE RESPONSABILIDADE -->
        <div class="section-header mb-3 mt-6">Termo de Responsabilidade</div>
        <v-checkbox
          v-model="term_agreement"
          @update:modelValue="saveTermsState"
          density="compact"
          hide-details="auto"
          class="mb-2"
        >
          <template #label>
            <span>Declaro que estou de acordo com os termos do edital e estou ciente dos critérios de seleção apresentados no site da pós-graduação.</span>
          </template>
        </v-checkbox>
        <v-checkbox
          v-model="term_responsibility"
          @update:modelValue="saveTermsState"
          density="compact"
          hide-details="auto"
          class="mb-4"
        >
          <template #label>
            <span>Declaro que é de minha inteira responsabilidade a conferência das informações e dos arquivos, e que posso ser desqualificado por fornecer informações ou documentos incorretos.</span>
          </template>
        </v-checkbox>

        <!-- BOTÕES -->
        <v-row justify="end" class="mt-4" no-gutters>
          <v-col cols="auto" class="d-flex pa-0" style="gap: 8px">
            <v-btn color="secondary" @click="$emit('back')">Voltar</v-btn>
            <v-btn
              color="success"
              :loading="loading"
              :disabled="!term_agreement || !term_responsibility || processStatus !='started'"
              @click="saveForm(true)"
            >Finalizar inscrição
            </v-btn>
          </v-col>
        </v-row>

      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "axios";
import keycloak from "../../plugins/keycloak";
import { getRegistrationStatus } from '@/utils/registration';

export default {
  name: "ApplicationFormSpecial",
  props: ["processId"],

  data() {
    return {
      loading: false,
      valid: false,
      loadingClasses: false,
      classes: [],
      term_agreement: false,
      term_responsibility: false,
      processStatus: "",
      form: {
        id: null,
        user_id: null,
        process_id: this.processId,

        requested_disciplines: [],
        reason_to_take_discipline: "",

        graduation_course: "",
        graduation_institution: "",
        graduation_year: "",

        is_postgraduate_student: false,
        postgraduate_course: "",
        postgraduate_advisor: "",
        postgraduate_project_title: "",
        postgraduate_semesters_completed: "",

        is_employed: false,
        job_type: "",
        workplace: "",
        job_position: "",
        working_since: "",

        has_special_needs: false,
        special_needs_description: ""
      }
    };
  },

  async created() {
    await this.loadProcessData();
    await this.loadClasses();

    const uuid = keycloak?.tokenParsed?.sub;
    if (uuid) {
      this.form.user_id = await this.getUserId(uuid);
      await this.loadExistingApplication();
      this.loadTermsState();
    }
  },

  watch: {
    "form.is_postgraduate_student"(val) {
      if (!val) {
        this.form.postgraduate_course = "";
        this.form.postgraduate_advisor = "";
        this.form.postgraduate_project_title = "";
        this.form.postgraduate_semesters_completed = "";
      }
    },
    "form.is_employed"(val) {
      if (!val) {
        this.form.job_type = "";
        this.form.workplace = "";
        this.form.job_position = "";
        this.form.working_since = "";
      }
    },    "form.requested_disciplines"(val) {
      if (Array.isArray(val) && val.length > 2) {
        this.form.requested_disciplines = val.slice(0, 2);
      }
    },    // "form.has_special_needs"(val) {
    //   if (!val) {
    //     this.form.special_needs_description = "";
    //   }
    // }
  },

  methods: {
    normalizeDateForInput(value) {
      if (!value) return "";

      if (typeof value === "string") {
        const isoMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoMatch) return isoMatch[1];

        const brMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (brMatch) {
          const [, day, month, year] = brMatch;
          return `${year}-${month}-${day}`;
        }
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    async loadProcessData() {
      const actives = this.$store.state.activeProcesses;
      const process = actives[this.processId];
      if (!process) return;
      this.processStatus = getRegistrationStatus(process.phases);
    },

    async loadClasses() {
      this.loadingClasses = true;
      try {
        const res = await axios.get(
          `${this.$APIPATH}/processes/${this.processId}/classes`, { skipAuth: true }
        );

        const list = res.data?.data || [];
        this.classes = list.map(c => ({
          id: c.id,
          label: `${c.code} - ${c.name}`
        }));
      } catch (err) {
        console.error("Erro ao carregar disciplinas:", err);
        this.$toast.error("Erro ao carregar disciplinas. Tente recarregar a página.");
      } finally {
        this.loadingClasses = false;
      }
    },

    async getUserId(uuid) {
      const res = await axios.get(`${this.$PROFPATH}/profiles`, {
        params: {
          filters: {
            kc_uuid: uuid,
          },
        },
      });
      return res.data?.data?.[0]?.id ?? null;
    },

    async saveForm(next) {
      try {
        // Validação do v-form
        if (!this.$refs.form.validate()) return;

        const requiredFields = [
          "graduation_course",
          "graduation_institution",
          "graduation_year",
          "requested_disciplines",
          "reason_to_take_discipline"
        ];

        const missingFields = requiredFields.filter(field => {
          const value = this.form[field];
          if (Array.isArray(value)) return value.length === 0;
          return value === null || value === undefined || value.toString().trim() === "";
        });

        if (!this.valid || missingFields.length > 0) {
          this.$toast.error(
            "Por favor, preencha todos os campos obrigatórios!"
          );
          console.warn("Campos faltando:", missingFields);
          return; // bloqueia envio
        }

        if (Array.isArray(this.form.requested_disciplines) && this.form.requested_disciplines.length > 2) {
          this.$toast.error("Selecione no máximo 2 disciplinas.");
          this.form.requested_disciplines = this.form.requested_disciplines.slice(0, 2);
          return; // bloqueia envio
        }

        this.loading = true;

        // URL para POST ou PUT
        const url = this.form.id
          ? `${this.$BACKPATH}/special-applications/${this.form.id}?_method=PUT`
          : `${this.$BACKPATH}/special-applications`;

        // Chama backend apenas se tudo estiver preenchido
        await axios.post(url, {
          ...this.form,
          term_agreement: this.term_agreement,
          term_responsibility: this.term_responsibility,
        }, {
          headers: {
            Authorization: `Bearer ${keycloak.token}`
          }
        });

        // Armazena a inscrição salva no localStorage para referência na Home
        const cacheKey = `special-application-${this.form.user_id}-${this.form.process_id}`;
        localStorage.setItem(cacheKey, JSON.stringify(this.form));

        this.$toast.success("Inscrição salva com sucesso!");
        if (next) this.$emit("next");

      } catch (err) {
        console.error("Erro ao salvar inscrição:", err);
        this.$toast.error(this.getBackendErrorMessage(err));
      } finally {
        this.loading = false;
      }
    }


    ,

    async loadExistingApplication() {
      if (!this.form.user_id || !this.form.process_id) return;

      try {
        const res = await axios.get(
          `${this.$BACKPATH}/special-applications`,
          {
            params: {
              "filters[user_id]": this.form.user_id,
              "filters[process_id]": this.form.process_id
            },
            headers: {
              Authorization: `Bearer ${keycloak.token}`
            }
          }
        );

        const app = res.data?.data?.[0];
        if (!app) return;

        Object.assign(this.form, app);
        this.term_agreement = !!app.term_agreement;
        this.term_responsibility = !!app.term_responsibility;
        this.form.working_since = this.normalizeDateForInput(this.form.working_since);
        if (Array.isArray(this.form.requested_disciplines) && this.form.requested_disciplines.length > 2) {
          this.form.requested_disciplines = this.form.requested_disciplines.slice(0, 2);
        }

      } catch (err) {
        console.error("Erro ao carregar inscrição especial:", err);
        this.$toast.error("Erro ao carregar dados da inscrição. Tente recarregar a página.");
      }
    },

    onRequestedDisciplinesChange(val) {
      if (Array.isArray(val) && val.length > 2) {
        this.$toast.error("Selecione no máximo 2 disciplinas.");
        this.form.requested_disciplines = val.slice(0, 2);
        return;
      }
      this.form.requested_disciplines = val;
    },

    getBackendErrorMessage(err) {
      const data = err.response?.data;
      if (!data) return "Erro de conexão. Verifique sua internet e tente novamente.";
      if (data.errors) {
        const messages = Object.values(data.errors).flat();
        return messages.join(" | ");
      }
      return data.message || "Erro inesperado. Tente novamente.";
    },

    loadTermsState() {
      const key = `terms-accepted-${this.form.user_id}-${this.form.process_id}`;
      const saved = localStorage.getItem(key);
      if (!saved) return;
      const { term1, term2 } = JSON.parse(saved);
      this.term_agreement = !!term1;
      this.term_responsibility = !!term2;
    },

    saveTermsState() {
      const key = `terms-accepted-${this.form.user_id}-${this.form.process_id}`;
      localStorage.setItem(key, JSON.stringify({ term1: this.term_agreement, term2: this.term_responsibility }));
    },

  }
};
</script>

<style scoped>
.section-header {
  background: #e3f2fd;
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: bold;
  color: #1976d2;
}
</style>
